/* The garage.
 *
 * One shopper, one vehicle, remembered across the site. Everything else here
 * hangs off that: the header shows it, the product page checks against it, and
 * collection links carry it as a filter.
 *
 * Three ideas worth knowing before reading the code.
 *
 * 1. The vehicle tree is already in the page (garage-data.liquid), so choosing
 *    a model, a year and a trim costs no network request. About a hundred
 *    vehicles is small enough to ship, and that is only true because this store
 *    sells for two platforms.
 *
 * 2. Shopify's storefront filter matches a metaobject by GID, not by handle,
 *    so the GID is what builds a filter URL. The data ships bare numeric ids
 *    and this file rebuilds the GID.
 *
 * 3. The fit check has THREE answers, not two: fits, does not fit, and not
 *    verified. The third is for a product carrying no fitment data at all, and
 *    it is not optional. The Turn 14 app's own definition of "universal"
 *    covers parts that simply have no data, so "no data" and "fits everything"
 *    arrive looking identical. Rendering them the same way would be a fit claim
 *    the catalogue cannot support, which is what CLAUDE.md 7.3 and 9.3 forbid.
 */
;(function () {
  var KEY = 'ac_garage'
  var LIST_KEY = 'ac_garage_list'
  /* Session, not local, and deliberately: "show me everything" is a decision
     about this visit, not a standing preference. A shopper who comes back
     tomorrow wants their car back. */
  var SHOW_ALL_KEY = 'ac_garage_show_all'
  var EVENT = 'ac:garage:change'
  var FILTER = 'filter.p.m.custom.fitment'

  /* The tree is a committed, CDN-cached asset (garage-vehicles.js), not Liquid
     rendered from shop.metaobjects. That is not a preference: shop.metaobjects
     returns AT MOST 50 entries, which silently truncated a 102 vehicle list
     down to two Mustang years and every F-150 one, with no error anywhere. */
  function tree() {
    return window.ACGarageData || null
  }

  function gid(id) {
    var t = tree()
    return (t ? t.prefix : 'gid://shopify/Metaobject/') + id
  }

  /* ---- stale ids, healed by name ----------------------------------------
     A saved vehicle keeps the metaobject id it had on the day it was saved,
     and that id is not forever: rebuilding the metaobjects (a re-import, a
     taxonomy change) issues new ids. Without this, the morning after a rebuild
     every shopper with a saved car would see "Doesn't fit" on every product,
     because their stored id matches nothing, and nothing on screen would say
     the car is stale rather than the parts wrong.

     So storage is never trusted over the tree. An id the tree does not know is
     re-matched by what the shopper actually chose (model, year, trim), which
     survives a rebuild because it is the identity of the car, not a database
     handle for it. Only a vehicle whose name matches nothing either (a scope
     change removed it) is dropped, and then the header honestly shows "Select
     vehicle" instead of a false verdict. */
  var idIndex = null
  function nodeFor(id) {
    var t = tree()
    if (!t) return null
    if (!idIndex) {
      idIndex = {}
      t.models.forEach(function (m) {
        m.years.forEach(function (y) {
          y.trims.forEach(function (tr) {
            idIndex[String(tr.id)] = tr
          })
        })
      })
    }
    return idIndex[String(id)] || null
  }

  /* Same collapsing the importer uses: case, punctuation and whitespace are
     not identity, so "GT-350" and "gt350" meet in the middle. */
  function norm(s) {
    return String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9]/g, '')
  }

  function rematch(v) {
    var t = tree()
    if (!t || !v) return null
    for (var mi = 0; mi < t.models.length; mi++) {
      var m = t.models[mi]
      if (m.key !== v.model && norm(m.name) !== norm(v.model)) continue
      for (var yi = 0; yi < m.years.length; yi++) {
        var y = m.years[yi]
        if (Number(y.year) !== Number(v.year)) continue
        for (var ti = 0; ti < y.trims.length; ti++) {
          var tr = y.trims[ti]
          if (norm(tr.trim) === norm(v.trim) || norm(tr.name) === norm(v.name)) {
            return { id: tr.id, name: tr.name, model: m.key, year: y.year, trim: tr.trim }
          }
        }
      }
    }
    return null
  }

  function healOne(v) {
    if (!v || !v.id) return null
    if (!tree()) return v /* no tree loaded, no verdict: leave it alone */
    if (nodeFor(v.id)) return v
    return rematch(v)
  }

  /* How many products carry this vehicle, per the last tree build. null means
     the build shipped no counts, which callers must read as "unknown", never
     as zero: unknown keeps the current behaviour, zero changes it. */
  function countFor(id) {
    var node = nodeFor(id)
    if (!node) return null
    return typeof node.n === 'number' ? node.n : null
  }

  /* localStorage throws outright in some privacy modes, so every read and write
     is guarded. A shopper with it blocked gets a garage that forgets between
     pages, which is worse than remembering and far better than a broken site. */
  /* The last vehicle this pageview knows about. get() reads storage every
     time, and a blocked or full storage makes every read return null: the
     shopper picks a car, sees it everywhere, and then the first carousel that
     injects markup triggers a repaint that reads null and silently wipes every
     verdict on screen. This is the fallback the comment in set() always
     promised and never had. */
  var memory = null
  var listMemory = []

  function get() {
    try {
      var raw = window.localStorage.getItem(KEY)
      if (raw) {
        var v = JSON.parse(raw)
        if (v && v.id) return v
      }
    } catch (e) {
      /* Blocked or full: fall through to what this pageview remembers. */
    }
    return memory
  }

  /* html.garage-has-vehicle: lets CSS hide what is only for a shopper with no
     car saved (the "Fits ..." line under a product title) before first paint.
     This script runs blocking in the head, so a returning shopper never sees
     that line flash and then vanish. Kept in step by set() below. */
  function markRoot(vehicle) {
    document.documentElement.classList.toggle('garage-has-vehicle', !!vehicle)
  }

  function set(vehicle) {
    memory = vehicle || null
    markRoot(memory)
    try {
      if (vehicle) window.localStorage.setItem(KEY, JSON.stringify(vehicle))
      else window.localStorage.removeItem(KEY)
    } catch (e) {
      /* Ignore: the in-page state below still updates for this pageview. */
    }
    /* Choosing a car is the opposite of asking to see everything, so it clears
       that. Without this a shopper who browsed everything once would never get
       their filter back without reloading. */
    if (vehicle) showAll(false)
    document.dispatchEvent(new CustomEvent(EVENT, { detail: vehicle || null }))
  }

  /* ---- the garage holds more than one car -----------------------------
     It held exactly one, and adding a second silently dropped the first, which
     is not a garage. The active vehicle still lives under its own key, so a
     shopper who had one before this change keeps it and the list seeds itself
     from it on first read: nothing to migrate and nothing to lose. */
  function list() {
    var saved = []
    try {
      saved = JSON.parse(window.localStorage.getItem(LIST_KEY) || '[]')
    } catch (e) {
      saved = listMemory
    }
    if (!saved.length) saved = listMemory
    if (!Array.isArray(saved)) saved = []
    saved = saved.filter(function (v) {
      return v && v.id
    })
    var active = get()
    if (active && !saved.some(function (v) {
      return String(v.id) === String(active.id)
    })) {
      saved.unshift(active)
    }
    return saved
  }

  function saveList(arr) {
    listMemory = arr || []
    try {
      window.localStorage.setItem(LIST_KEY, JSON.stringify(arr))
    } catch (e) {
      /* Ignore: listMemory carries the garage for this pageview. */
    }
  }

  /* The startup validation pass, run once in the blocking phase before the
     redirect below reads the active vehicle. Storage writes only, no events:
     listeners do not exist yet, and set() would also clear the show-all flag,
     which is a shopper preference a data repair has no business touching. */
  function heal() {
    if (!tree()) return
    var changed = false
    var healed = []
    list().forEach(function (v) {
      var h = healOne(v)
      if (!h) {
        changed = true
        return
      }
      if (String(h.id) !== String(v.id)) changed = true
      healed.push(h)
    })
    if (changed) saveList(healed)
    var active = get()
    if (!active) return
    var h = healOne(active)
    if (h && String(h.id) === String(active.id)) return
    /* Healed under a new id, or gone entirely: the next car in the garage
       takes over, and an empty garage becomes an honest empty garage. */
    try {
      var next = h || healed[0] || null
      if (next) window.localStorage.setItem(KEY, JSON.stringify(next))
      else window.localStorage.removeItem(KEY)
    } catch (e) {
      /* Ignore: this pageview then runs on the stale value, same as before. */
    }
  }

  /* Add and make active. Picking a car you already have just switches to it
     rather than filing a duplicate. */
  function add(vehicle) {
    if (!vehicle || !vehicle.id) return
    var next = list().filter(function (v) {
      return String(v.id) !== String(vehicle.id)
    })
    next.unshift(vehicle)
    saveList(next)
    set(vehicle)
  }

  /* Removing the active car promotes the next one rather than emptying the
     garage, because a shopper with two cars who deletes one still has a car. */
  function remove(id) {
    var next = list().filter(function (v) {
      return String(v.id) !== String(id)
    })
    saveList(next)
    var active = get()
    if (active && String(active.id) === String(id)) set(next[0] || null)
    else document.dispatchEvent(new CustomEvent(EVENT, { detail: get() }))
  }

  /* ---- "show me everything" -------------------------------------------- */
  /* Kept in localStorage, beside the garage itself, so it holds through a
     refresh, a new tab and tomorrow's visit (owner, Jam a7c9a881): a shopper
     who asked to see everything keeps seeing everything until they ask for
     their car back, and the listing bar offers that on every page. It was in
     sessionStorage, which a new tab does not share, so the filter came back
     on its own and read as the site ignoring a choice made one click ago. */
  function showAll(on) {
    try {
      if (on) window.localStorage.setItem(SHOW_ALL_KEY, '1')
      else window.localStorage.removeItem(SHOW_ALL_KEY)
      window.sessionStorage.removeItem(SHOW_ALL_KEY)
    } catch (e) {
      /* Ignore. Worst case the filter reapplies, which is the old behaviour. */
    }
  }

  function isShowingAll() {
    try {
      return window.localStorage.getItem(SHOW_ALL_KEY) === '1' ||
        window.sessionStorage.getItem(SHOW_ALL_KEY) === '1'
    } catch (e) {
      return false
    }
  }

  /* Is this a listing page the vehicle filter applies to? */
  function onListing() {
    var path = window.location.pathname
    return path.indexOf('/collections/') !== -1 && path.indexOf('/products/') === -1
  }

  /* Which vehicle is the page filtered on, if any. Compares the VALUE, not
     just the parameter's presence: a page filtered on the Mustang while the
     garage holds the F-150 is not "showing parts for your F-150", and saying
     so under a grid of Mustang parts is a false statement about what the
     shopper is looking at. Reachable from a bookmark, a shared link or the
     back button. */
  function filteredOn() {
    var raw = null
    try {
      raw = new URLSearchParams(window.location.search).get(FILTER)
    } catch (e) {
      var m = window.location.search.match(new RegExp('[?&]' + FILTER.replace(/\./g, '\\.') + '=([^&]*)'))
      raw = m ? decodeURIComponent(m[1]) : null
    }
    return raw || null
  }

  function isFiltered(vehicle) {
    var raw = filteredOn()
    if (!raw) return false
    return vehicle ? raw === gid(vehicle.id) : true
  }

  /* Is this product verified against the shopper's vehicle?
     Returns 'universal', 'fits', 'trims', 'no', 'unknown', or null when there
     is no vehicle to answer about (the paint draws that as the invite).
     Never guesses.

     'trims' is a yes with a condition: the vehicle is on the product, but the
     supplier's rows name only some trim levels (a Lariat and not an XLT), which
     the engine picker cannot tell apart. The product carries a generated note
     saying which (custom.fitment_notes, written by scripts/fitment/trim-note.js),
     and the page shows it instead of a plain yes. */
  function checkFit(fitmentIds, vehicle, universal, trimNote) {
    /* Universal comes first and does not consult the garage, because it is true
       whichever car the shopper has and true before they have picked one. It is
       an explicit flag the owner sets, never inferred from an empty fitment list:
       a part with no data and a part that fits everything arrive from the
       distributor looking identical, and only one of them is a claim we can make.
       Inferring it would turn every gap in the catalogue into a fit claim, which
       is exactly what the "unknown" state exists to prevent. */
    /* The universal flag is a claim about a product with no fitment list. When
       a product carries BOTH, the two data sources disagree, and answering
       "fits any vehicle" would pick the permissive one over the specific one.
       A contradiction is not a verified fit, so it reads as unverified. */
    if (universal && (!fitmentIds || !fitmentIds.length)) return 'universal'
    if (!vehicle) return null
    if (!fitmentIds || !fitmentIds.length) return 'unknown'
    if (universal) return fitmentIds.indexOf(String(vehicle.id)) === -1 ? 'unknown' : 'fits'
    if (fitmentIds.indexOf(String(vehicle.id)) === -1) return 'no'
    return trimNote ? 'trims' : 'fits'
  }

  function filterUrl(url, vehicle) {
    var u
    try {
      u = new URL(url, window.location.origin)
    } catch (e) {
      return url
    }
    u.searchParams.delete(FILTER)
    if (vehicle) u.searchParams.set(FILTER, gid(vehicle.id))
    /* A filter change invalidates the page cursor. Leaving it on sends the
       shopper to page 3 of a result set that now has one page. */
    u.searchParams.delete('page')
    return u.pathname + (u.search || '') + (u.hash || '')
  }

  /* ---- landing on a collection with a car in the garage ------------------
     Link rewriting covers a shopper who clicks through the site. It cannot cover
     one who types a URL, uses the back button, or arrives from search or an ad,
     and the owner asked that a chosen vehicle filter the catalogue however the
     shopper got there.

     This runs from a BLOCKING script in the head, before anything has painted,
     which is the whole reason it is in this file and not in garage-ui.js. From a
     deferred script the unfiltered page had already rendered, so the shopper saw
     the wrong products flash and then a second load. Here they see one page.

     Three guards, and each is a real case:
     - any filter parameter present means the shopper is actively filtering, and
       adding a vehicle on top of their choices would be the site arguing with
       them. It also means clearing the fitment chip while keeping another filter
       sticks, which is what a shopper expects from a chip they just removed.
     - "show me everything" this session means exactly that.
     - replace, never push, so the back button goes where they expect rather than
       into a redirect loop. */
  function applyToCollection() {
    var path = window.location.pathname
    if (path.indexOf('/collections/') === -1) return
    if (path.indexOf('/products/') !== -1) return
    if (window.location.search.indexOf('filter.') !== -1) return
    if (isShowingAll()) return
    /* Arriving by Back or Forward is not a fresh entry. Replacing the entry
       the shopper just navigated back to destroyed it, so pressing Back on a
       filtered page put the filter straight back and Back appeared to do
       nothing at all: there was no way out of the filter with the browser's
       own control. */
    try {
      var nav = performance.getEntriesByType('navigation')[0]
      if (nav && nav.type === 'back_forward') return
    } catch (e) {
      /* No navigation timing: fall through and behave as before. */
    }
    var vehicle = get()
    if (!vehicle) return
    /* Zero products carry this vehicle, so the filtered page is guaranteed
       empty. Redirecting into a guaranteed dead end is worse than not
       filtering: stay on the unfiltered collection and garage-ui says why.
       Only a literal zero skips; null means the build shipped no counts and
       the redirect behaves as it always has. */
    if (countFor(vehicle.id) === 0) return
    var next = filterUrl(window.location.href, vehicle)
    if (!next || next === window.location.pathname + window.location.search) return
    window.location.replace(next)
  }

  window.ACGarage = {
    KEY: KEY,
    EVENT: EVENT,
    FILTER: FILTER,
    tree: tree,
    gid: gid,
    get: get,
    set: set,
    list: list,
    add: add,
    remove: remove,
    showAll: showAll,
    isShowingAll: isShowingAll,
    onListing: onListing,
    isFiltered: isFiltered,
    filteredOn: filteredOn,
    clear: function () {
      set(null)
    },
    checkFit: checkFit,
    countFor: countFor,
    filterUrl: filterUrl,
    /* Fires once on load so anything listening can paint the stored vehicle
       without each component reading storage for itself. */
    announce: function () {
      /* Marked, because this fires on EVERY page load with the stored
         vehicle and listeners must be able to tell it from an actual change.
         Without the mark, "Show everything" was undone the moment the
         unfiltered page finished loading: the announcement looked exactly
         like the shopper picking their car again. */
      var event = new CustomEvent(EVENT, { detail: get() })
      event.announced = true
      document.dispatchEvent(event)
    },
  }

  /* Always on a later turn, never synchronously.
     These scripts are deferred, so by the time this one runs the document is
     already 'interactive', not 'loading'. Announcing straight away therefore
     fired the event BEFORE garage-ui.js had loaded and subscribed, so a stored
     vehicle painted nothing on a fresh page load: pick a car and it showed,
     refresh and it was gone. It looked like storage failing and was really a
     listener that was not there yet.

     garage-ui.js also paints once from get() when it initialises, so the first
     paint does not depend on this event arriving at the right moment at all. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', window.ACGarage.announce)
  } else {
    setTimeout(window.ACGarage.announce, 0)
  }

  heal()
  markRoot(get())
  applyToCollection()
})()
